import React from "react";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { saveAs } from "file-saver";
import { useApolloClient, useMutation } from "@apollo/client";

import { Card, FilterGroups, FilterValues, PageHeader, Table, FilterTypes, QuickFilterTypes } from "@cofano/cck";
import { useCoastguard } from "@cofano/coastguard2-frontend-client";

import { Box } from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import { TableId, useBackendTable } from "components/table";
import Helmet from "components/helmet/Helmet";
import CommentsWidget from "components/comments/CommentsWidget";
import useComments from "components/comments/useComments";
import CommentIndicatorButton from "components/comments/CommentIndicatorButton";

import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import StatusChanger from "view/statuses/components/changer/StatusChanger";
import ProductSelect, { ProductSelectOption } from "view/products/components/select/ProductSelect";
import RelationSelect, { RelationSelectOption } from "view/relations/components/selects/RelationSelect";
import LocationSelect, { LocationSelectOption } from "view/locations/components/selects/LocationSelect";
import FleetSelect, { FleetSelectOption } from "view/fleets/components/selects/FleetSelect";
import StatusSelect, { StatusSelectOption } from "view/statuses/components/selects/StatusSelect";
import CargosDownload from "view/cargos/components/download/CargosDownload";
import { CargoActionDate } from "view/cargos/components/date/CargoActionDate";

import { DataFormat, Fields, hasOptionalFieldPermission, hasPluginPermission, Language } from "imports/Utils";

import { LocationLink, ProductLink, RelationLink, ShipLink } from "utils/LinkUtils";
import { FormattedDateWindow } from "utils/DateUtils";

import { ChildPropsFromPagination } from "types/commons";

import { GetNominationsDocument, GetNominationsQuery } from "__generated__/view/nominations/graphql/nomination.queries.generated";
import { SetNominationChangedDocument } from "__generated__/view/nominations/graphql/nomination.mutations.generated";
import { CommentEntityType, RelationType, SortDirection, StatusType } from "__generated__/enums";

type CargoFromQuery = ChildPropsFromPagination<GetNominationsQuery, "cargos">

const Nominations = () => {

	const client = useApolloClient();
	const { instance } = useCoastguard();
	const session = useStoreState(state => state.session);

	const { statuses } = session;

	const [ setChangesViewed ] = useMutation(SetNominationChangedDocument);

	const { tableProps, filterProps, filterValuesProps, filters, refetch, clearSelection } = useBackendTable({
		id: TableId.Nominations,
		selectSettings: {
			isFullSelectEnabled: true,
		},
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetNominationsDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "firstLoadingAction.start",
								direction: SortDirection.Desc,
							} ],
						},
						search: {
							...filter,
							fleets: filter.fleets?.map((fleet: FleetSelectOption) => fleet.id),
							currentStatuses: filter.currentStatuses?.map((status: StatusSelectOption) => status.id),
							suppliers: filter.supplier?.map((relation: RelationSelectOption) => relation.id),
							loadingLocations: filter.loadingLocations?.map((location: LocationSelectOption) => location.id),
							dischargingLocations: filter.dischargingLocations?.map((location: LocationSelectOption) => location.id),
							products: filter.product?.map((product: ProductSelectOption) => product.id),
							ships: filter.ships?.map((ship: ShipSelectOption) => ship.id),
							loadingWindow: filter.loadingWindow ? {
								from: filter.loadingWindow[0],
								to:  filter.loadingWindow[1],
							} : undefined,
							dischargingWindow: filter.dischargingWindow ? {
								from: filter.dischargingWindow[0],
								to:  filter.dischargingWindow[1],
							} : undefined,
						},
					},
				});

				callback({
					content: result.data.cargos?.content ?? [],
					totalResults: result.data.cargos?.totalElements || 0,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const { commentsProps, openComment } = useComments({
		cargos: tableProps.content as CargoFromQuery[],
		onChange: () => refetch(),
	});

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("NOMINATIONS")}
					<Helmet title={intl.formatMessage({ id: "NOMINATIONS" })} />
				</PageHeader.Title>
			</PageHeader>

			{filterValuesProps.values.length > 0 && (
				<Box mb={2}>
					<Card disablePadding>
						<FilterValues
							{...filterValuesProps}
							fields={[ {
								id: "fleets",
								title: intl.formatMessage({ id: "FLEETS_SINGLE" }),
								renderFilterValue: (value) => value?.map((fleet: FleetSelectOption) => fleet.description).join(", "),
							}, {
								id: "currentStatuses",
								title: intl.formatMessage({ id: "STATUS" }),
								renderFilterValue: (value) => value?.map((status: StatusSelectOption) => status.description).join(", "),
							}, {
								id: "supplier",
								title: intl.formatMessage({ id: "SUPPLIER" }),
								renderFilterValue: (value) => value?.map((relation: RelationSelectOption) => relation.name).join(", "),
							}, {
								id: "loadingLocations",
								title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
								renderFilterValue: (value) => value?.map((location: LocationSelectOption) => location.description).join(", "),
							}, {
								id: "dischargingLocations",
								title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
								renderFilterValue: (value) => value?.map((location: LocationSelectOption) => location.description).join(", "),
							}, {
								id: "product",
								title: intl.formatMessage({ id: "PRODUCT" }),
								renderFilterValue: (value) => value?.map((product: ProductSelectOption) => product.name).join(", "),
							}, {
								id: "ships",
								title: intl.formatMessage({ id: "SHIP" }),
								renderFilterValue: (value) => value?.map((ship: ShipSelectOption) => ship.name).join(", "),
							}, {
								id: "loadingWindow",
								title: intl.formatMessage({ id: "ORDER_LOADING_DATE" }),
								renderFilterValue: (value) => <FormattedDateWindow from={value[0]} to={value[1]} withoutYear />,
							}, {
								id: "dischargingWindow",
								title: intl.formatMessage({ id: "ORDER_DISCHARGING_DATE" }),
								renderFilterValue: (value) => <FormattedDateWindow from={value[0]} to={value[1]} withoutYear />,
							}, {
								id: "nominationChanged",
								title: intl.formatMessage({ id: "NOMINATION_CHANGED" }),
								renderFilterValue: (value) => DataFormat.booleanAsText(value),
							} ]} />
					</Card>
				</Box>
			)}

			<Card disablePadding>
				<Table
					{...tableProps}
					filterComponent={() => (
						<FilterGroups
							{...filterProps}
							fields={[ {
								id: "fleets",
								title: intl.formatMessage({ id: "FLEETS_SINGLE" }),
								render: (value, onChange) =>
									<FleetSelect
										value={value}
										onChange={(options) => onChange(options)}
										multiple />,
								disabled: !hasPluginPermission(session, "fleets"),
							}, {
								id: "currentStatuses",
								title: intl.formatMessage({ id: "STATUS" }),
								render: (value, onChange) => (
									<StatusSelect
										value={value}
										onChange={(options) => onChange(options)}
										statuses={statuses?.CARGO ?? []}
										multiple />
								),
								disabled: !hasPluginPermission(session, "cargoStatuses"),
							}, {
								id: "supplier",
								title: intl.formatMessage({ id: "SUPPLIER" }),
								render: (value, onChange) =>
									<RelationSelect
										value={value}
										onChange={(options) => onChange(options)}
										types={[ RelationType.Supplier ]}
										multiple />,
								disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "supplier"),
							}, {
								id: "loadingLocations",
								title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
								render: (value, onChange) =>
									<LocationSelect
										value={value}
										onChange={(options) => onChange(options)}
										multiple />,
							}, {
								id: "dischargingLocations",
								title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
								render: (value, onChange) =>
									<LocationSelect
										value={value}
										onChange={(options) => onChange(options)}
										multiple />,
							}, {
								id: "product",
								title: intl.formatMessage({ id: "PRODUCT" }),
								render: (value, onChange) =>
									<ProductSelect
										value={value}
										onChange={(options) => onChange(options)}
										multiple />,
							}, {
								id: "ships",
								title: intl.formatMessage({ id: "SHIP" }),
								render: (value, onChange) =>
									<ShipSelect
										value={value}
										onChange={(options) => onChange(options)}
										multiple />,
							}, {
								id: "loadingWindow",
								title: intl.formatMessage({ id: "ORDER_LOADING_DATE" }),
								filterType: FilterTypes.DATE_RANGE,
							}, {
								id: "dischargingWindow",
								title: intl.formatMessage({ id: "ORDER_DISCHARGING_DATE" }),
								filterType: FilterTypes.DATE_RANGE,
							}, {
								id: "nominationChanged",
								title: intl.formatMessage({ id: "NOMINATION_CHANGED" }),
								filterType: FilterTypes.BOOLEAN,
							} ]} />
					)}
					columns={[ {
						id: "id",
						title: intl.formatMessage({ id: "CARGO_ID" }),
						sortable: true,
						filterComponent: QuickFilterTypes.NUMBER,
					}, {
						id: "currentStatuses",
						title: intl.formatMessage({ id: "STATUS" }),
						render: (row: CargoFromQuery) => (
							<StatusChanger
								session={session}
								entity={row}
								statusType={StatusType.Cargo}
								refetch={refetch} />
						),
						sortable: true,
						sortKey: "lastStatusChange.status.description",
						width: 200,
						disabled: !hasPluginPermission(session, "cargoStatuses"),
						filterComponent: (value, onChange) => (
							<StatusSelect
								label={intl.formatMessage({ id: "STATUS" })}
								value={value}
								onChange={(options) => onChange(options)}
								statuses={statuses?.CARGO ?? []}
								multiple />
						),
					}, {
						id: "supplier",
						title: intl.formatMessage({ id: "SUPPLIER" }),
						render: (row: CargoFromQuery) => <RelationLink relation={row.supplier} />,
						sortable: true,
						sortKey: "supplier.name",
						width: 200,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "supplier"),
					}, {
						id: "loadingLocations",
						title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
						render: (row: CargoFromQuery) => <LocationLink location={row.firstLoadingAction?.location} />,
						sortable: true,
						sortKey: "firstLoadingAction.location.description",
						width: 200,
					}, {
						id: "dischargingLocations",
						title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
						render: (row: CargoFromQuery) => <LocationLink location={row.lastDischargingAction?.location} />,
						sortable: true,
						sortKey: "lastDischargingAction.location.description",
						width: 200,
					}, {
						id: "product",
						title: intl.formatMessage({ id: "PRODUCT" }),
						render: (row: CargoFromQuery) => <ProductLink product={row.product} />,
						sortable: true,
						sortKey: "product.name",
						width: 200,
						filterComponent: (value, onChange) => (
							<ProductSelect
								label={intl.formatMessage({ id: "PRODUCT" })}
								value={value}
								onChange={(options) => onChange(options)}
								multiple />
						),
					}, {
						id: "volume",
						title: intl.formatMessage({ id: "CARGO_VOLUME" }),
						render: (row: CargoFromQuery) => DataFormat.volumeM3(row.volume),
						width: 200,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "volume"),
					}, {
						id: "weight",
						title: intl.formatMessage({ id: "WEIGHT" }),
						render: (row: CargoFromQuery) => DataFormat.tonnage(row.weight),
						width: 200,
						disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "weight"),
					}, {
						id: "comments",
						title: intl.formatMessage({ id: "COMMENTS" }),
						render: (row: CargoFromQuery) => row.comments?.[row.comments.length - 1]?.comment,
						width: 200,
					}, {
						id: "preCargos_1",
						title: intl.formatMessage({ id: "LAST_SHIPPED_PRODUCT_1" }),
						render: (row: CargoFromQuery) => <ProductLink product={row.preCargos?.[0]} />,
						width: 200,
					}, {
						id: "preCargos_2",
						title: intl.formatMessage({ id: "LAST_SHIPPED_PRODUCT_2" }),
						render: (row: CargoFromQuery) => <ProductLink product={row.preCargos?.[1]} />,
						width: 200,
					}, {
						id: "preCargos_3",
						title: intl.formatMessage({ id: "LAST_SHIPPED_PRODUCT_3" }),
						render: (row: CargoFromQuery) => <ProductLink product={row.preCargos?.[2]} />,
						width: 200,
					}, {
						id: "ships",
						title: intl.formatMessage({ id: "SHIP" }),
						render: (row: CargoFromQuery) => <ShipLink ship={row.firstLoadingAction?.call?.voyage?.ship} />,
						sortable: true,
						sortKey: "firstLoadingAction.call.voyage.ship.name",
						width: 200,
						filterComponent: (value, onChange) => (
							<ShipSelect
								label={intl.formatMessage({ id: "SHIP" })}
								value={value}
								onChange={(options) => onChange(options)}
								multiple />
						),
					}, {
						id: "ship.eni",
						title: intl.formatMessage({ id: "ORDER_SHIP_ENI" }),
						render: (row: CargoFromQuery) => row.firstLoadingAction?.call?.voyage?.ship.eni,
						sortable: true,
						sortKey: "firstLoadingAction.call.voyage.ship.eni",
						width: 200,
					}, {
						id: "loadingWindow",
						title: intl.formatMessage({ id: "ORDER_LOADING_DATE" }),
						render: (row: CargoFromQuery) => <CargoActionDate action={row.firstLoadingAction} withoutYear />,
						sortable: true,
						sortKey: "firstLoadingAction.start",
						width: 300,
						filterComponent: QuickFilterTypes.DATE_RANGE,
					}, {
						id: "dischargingWindow",
						title: intl.formatMessage({ id: "ORDER_DISCHARGING_DATE" }),
						render: (row: CargoFromQuery) => <CargoActionDate action={row.lastDischargingAction} withoutYear />,
						sortable: true,
						sortKey: "lastDischargingAction.end",
						width: 200,
					}, {
						id: "nominationChanged",
						title: intl.formatMessage({ id: "NOMINATION_CHANGED" }),
						render: (row: CargoFromQuery) => DataFormat.boolean(row.nominationChanged),
						sortable: true,
						filterComponent: QuickFilterTypes.BOOLEAN,
						width: 150,
					} ]}
					rowActions={[ {
						icon: <InsertDriveFileIcon />,
						onClick: (row) => {

							const url = `/export/rtr/download/${row.id}?document=1`;

							instance.fetchBlob(url, {
								method: "GET",
							}).then(async (blob) => {
								saveAs(blob, "ExportNomination.pdf");
							});
						},
					}, {
						icon: <React.Fragment />,
						onClick: () => {},
						render: (row: CargoFromQuery) => (
							<CommentIndicatorButton
								entity={row}
								onClick={() => openComment(row.id, CommentEntityType.Cargo)}
								session={session} />
						),
					} ]}
					actions={[ {
						title: intl.formatMessage({ id: "NOMINATIONS_MARK_CHANGE_AS_VIEWED" }),
						disabled: (selection: CargoFromQuery[]) => selection.some(cargo => !cargo.nominationChanged),
						onClick: (selection: CargoFromQuery[] | null) => {
							setChangesViewed({
								variables: {
									cargoIds: selection?.map(cargo => cargo.id) ?? [],
								},
							}).then(() => {
								refetch();
							});
						},
					}, {
						render: (selection, isFullSelect, _event, state) =>
							<CargosDownload
								selection={selection || []}
								isFullSelect={isFullSelect}
								filters={filters}
								columns={state.columnSettings.columns}
								totalResults={tableProps.paginationSettings.totalResults}
								onDownload={() => clearSelection()} />,
					} ]} />

				<CommentsWidget {...commentsProps} />
			</Card>
		</div>
	);
};

export default Nominations;
